import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ARTIST_NAME, IMG, RELEASES, VIDEOS } from "@/data/content";
import { Reveal, ChapterLabel, PageHero, Tilt, FakePlayer, ImageReveal, MaskLines, usePageTitle } from "@/components/Shared";

const MusicPage = () => {
  const { t, lp, lf } = useLang();
  usePageTitle(`${t.nav.music} — ${ARTIST_NAME}`);
  const featured = RELEASES.find((r) => r.featured);
  const others = RELEASES.filter((r) => !r.featured);

  return (
    <div data-testid="music-page">
      <PageHero eyebrow={t.music.eyebrow} title={t.music.title} img={IMG.music}>
        <Reveal delay={0.4}>
          <p className="font-sans text-sm leading-relaxed text-[#A1A1AA]">{t.music.intro}</p>
        </Reveal>
      </PageHero>

      {/* Featured release — art object */}
      <section className="relative overflow-hidden py-28 sm:py-36">
        <span className="pointer-events-none absolute left-0 top-10 select-none whitespace-nowrap font-serif text-[13vw] leading-none text-outline opacity-50">
          {featured.title}
        </span>
        <div className="relative z-10 grid items-center gap-14 px-6 sm:px-12 lg:grid-cols-12 lg:px-20">
          <div className="lg:col-span-5">
            <Reveal>
              <Tilt max={9}>
                <div className="-rotate-2 shadow-[0_80px_120px_-40px_rgba(0,0,0,0.9)]">
                  <img src={featured.cover} alt={featured.title} loading="lazy" className="aspect-square w-full object-cover" />
                </div>
              </Tilt>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal><ChapterLabel no="01" label={t.music.featuredLabel} /></Reveal>
            <MaskLines text={featured.title} className="font-serif text-6xl font-light leading-none text-[#F9F8F6] sm:text-7xl" />
            <Reveal delay={0.15}>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.35em] text-[#71717A]">
                {lf(featured.format)} · {featured.year}
              </p>
            </Reveal>
            <Reveal delay={0.22} className="mt-10"><FakePlayer src={featured.preview} links={featured.links} /></Reveal>
            <Reveal delay={0.3} className="mt-10">
              <Link
                to={lp("/music/discography")}
                data-testid="music-discography-link"
                className="group inline-flex items-center gap-3 font-sans text-[11px] font-medium uppercase tracking-[0.3em] text-[#C5A059]"
              >
                {t.music.discoLink}
                <span className="h-px w-8 bg-[#C5A059] transition-all duration-500 group-hover:w-16" />
                <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Other releases — offset rhythm */}
      <section className="border-t border-white/5 px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal><ChapterLabel no="02" label={t.music.latestLabel} /></Reveal>
        <div className="mt-14 grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
          {others.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.1} className={i === 1 ? "lg:mt-24" : i === 2 ? "lg:mt-12" : ""}>
              <Link to={lp("/music/discography")} data-cursor="VIEW" className="group block">
                <Tilt max={6}>
                  <div className="relative aspect-square overflow-hidden shadow-2xl shadow-black/60">
                    <img src={r.cover} alt={r.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 flex items-center justify-center bg-[#0B0B0C]/0 opacity-0 transition-all duration-500 group-hover:bg-[#0B0B0C]/45 group-hover:opacity-100">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#C5A059]/70 bg-[#0B0B0C]/70">
                        <Play className="ml-0.5 h-5 w-5 text-[#C5A059]" />
                      </span>
                    </div>
                  </div>
                </Tilt>
                <div className="mt-6 flex items-baseline justify-between">
                  <p className="font-serif text-2xl font-light text-[#F9F8F6] transition-colors group-hover:text-[#C5A059]">{r.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">{r.year}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Videos strip */}
      <section className="border-t border-white/5 bg-[#101012] px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal><ChapterLabel no="03" label={t.nav.video} /></Reveal>
          <Reveal delay={0.1}>
            <Link to={lp("/music/video")} data-testid="music-video-link" className="group inline-flex items-center gap-3 font-sans text-[11px] font-medium uppercase tracking-[0.3em] text-[#C5A059]">
              {t.music.videoLink}
              <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-2" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {VIDEOS.slice(0, 3).map((v, i) => (
            <Reveal key={v.id} delay={i * 0.1} className={i === 1 ? "lg:mt-14" : ""}>
              <Link to={lp("/music/video")} data-cursor="PLAY" className="group relative block aspect-video overflow-hidden">
                <img src={v.img} alt={v.title} loading="lazy" className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <p className="font-serif text-lg font-light text-[#F9F8F6]">{v.title}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{lf(v.sub)}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default MusicPage;
